/**
 * AdminUrlsPanel — Cheat-sheet of every admin & ops surface in the app.
 *
 * Mounted as the "Admin URLs" tab in /admin. One bookmark, one place to find
 * everything — Editorial · Console · Table modules · Club modules · public
 * /club URLs across tenants.
 *
 * Founder spec (11 Jun 2026): "we should have all this on one page right"
 */

import React, { useMemo, useState } from "react";

export default function AdminUrlsPanel() {
  const [copied, setCopied] = useState(null);

  const origin = useMemo(() => {
    if (typeof window === "undefined") return "https://lesconcierge.com";
    return window.location.origin.replace(/\/$/, "");
  }, []);

  async function copy(text, key) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1400);
    } catch { /* ignore */ }
  }

  return (
    <div data-testid="admin-urls-panel" className="space-y-12">
      <header>
        <p className="eyebrow">Admin · Reference</p>
        <h2 className="font-serif text-3xl md:text-4xl mt-2">Admin URL map</h2>
        <p className="mt-3 text-[14px] text-[hsl(var(--muted))] max-w-2xl">
          Every admin & ops surface in the app, grouped by module. Click any URL to copy.
          Use this when onboarding a new team member, sharing a link, or briefing IT.
        </p>
      </header>

      {/* ─── ADMIN SHELL & CONSOLE ─────────────────────────── */}
      <UrlGroup
        eyebrow="Marketing & Console · core admin surfaces"
        title="Editorial desk & Concierge® Console"
        rows={[
          {
            url: `${origin}/admin`,
            who: "Marketing ops",
            what: "Editorial desk — content editor · pilot enquiries · Mira inbox · Clubs catalogue · The Bible · Admin URLs (this page)",
          },
          {
            url: `${origin}/admin/login`,
            who: "Sign-in",
            what: "Editorial desk login",
          },
          {
            url: `${origin}/mira/console`,
            who: "Concierge® team",
            what: "Live Mira conversations dashboard",
          },
          {
            url: `${origin}/mira/console/login`,
            who: "Sign-in",
            what: "Shared login for /mira/console and every /club/admin/* tool",
          },
        ]}
        copied={copied} onCopy={copy}
      />

      {/* ─── TABLE / RESTAURANTS ─────────────────────────── */}
      <UrlGroup
        eyebrow="Table · venues / Michelin / restaurants"
        title="Restaurant operations"
        rows={[
          {
            url: `${origin}/table`,
            who: "Members",
            what: "Public venues directory — Michelin distinctions, Confirmed by Concierge®, Near-me",
          },
          {
            url: `${origin}/table/admin`,
            who: "Ops",
            what: "Observability console · route watch · dish-query watch · curation watch",
          },
          {
            url: `${origin}/table/admin/confirm-queue`,
            who: "Ops",
            what: "Confirm queue — venues awaiting human confirmation",
          },
          {
            url: `${origin}/table/curate`,
            who: "Ops",
            what: "Magic Curation studio — venue editorial & whisper crafting",
          },
          {
            url: `${origin}/table/desk`,
            who: "Ops",
            what: "Table desk — request handling surface",
          },
          {
            url: `${origin}/admin#venues`,
            who: "Ops",
            what: "Venues catalogue admin panel · filter by Michelin distinction · toggle human_confirmed · audit trail (see Venues tab)",
          },
        ]}
        copied={copied} onCopy={copy}
      />

      {/* ─── CLUB SOCIAL ─────────────────────────── */}
      <UrlGroup
        eyebrow="Club Social® · partner-club network"
        title="Clubs · Members · Requests"
        rows={[
          {
            url: `${origin}/club/admin/clubs`,
            who: "Ops",
            what: "Clubs CRUD · 3,506 partner clubs · add/edit/delete · image upload · CSV import/export",
          },
          {
            url: `${origin}/club/admin/members`,
            who: "Ops",
            what: "Members CRUD · filter by tenant + status (incl. PENDING REVIEW) · CSV import/export · bulk-authorise · welcome email button",
          },
          {
            url: `${origin}/club/admin/requests`,
            who: "Ops",
            what: "Live visit request queue · every /club submission → Zoho ticket id + status",
          },
        ]}
        copied={copied} onCopy={copy}
      />

      {/* ─── ENJOY · live events + signature experiences ── */}
      <UrlGroup
        eyebrow="Enjoy® · live events + signature experiences"
        title="Browse · request · admin overlays"
        rows={[
          {
            url: `${origin}/enjoy`,
            who: "Members",
            what: "Discovery surface · tabs (Music · Theatre · Sports · Festivals · Family · Arts · Signature Experiences) · curated overlays + provider events · warm gatekeeping on Request",
          },
          {
            url: `${origin}/admin#enjoy-overlays`,
            who: "Ops",
            what: "Overlay CRUD · lc_events_overlays · confirm / archive / delete · CSV export (see Enjoy overlays tab)",
          },
        ]}
        copied={copied} onCopy={copy}
      />

      {/* ─── PUBLIC CLUB URLS ─────────────────────── */}
      <UrlGroup
        eyebrow="Public · Les Concierges members (lesconcierge.com)"
        title="What to send to LC members & new applicants"
        rows={[
          {
            url: "https://lesconcierge.com/club",
            who: "LC members + new applicants",
            what: "Sign-in gate (last name + card no) · 'Apply for membership →' link",
          },
          {
            url: "https://lesconcierge.com/club/signup",
            who: "New applicants",
            what: "Onboarding form · Identity / Contact / Residence / Family · all 'as per Aadhar'",
          },
        ]}
        copied={copied} onCopy={copy}
      />

      <UrlGroup
        eyebrow="Public · KVB (kvb.mindescapes.in · once DNS linked)"
        title="What to send to KVB members"
        rows={[
          {
            url: "https://kvb.mindescapes.in/",
            who: "KVB members",
            what: "Auto-redirect → /club",
          },
          {
            url: "https://kvb.mindescapes.in/club",
            who: "KVB members",
            what: "Sign-in gate only · no public Sign-up (KVB members come via CSV import)",
          },
        ]}
        copied={copied} onCopy={copy}
        muted
        note="Active once you link the domain in Manage Deployments → Domain (CNAME via Entri)."
      />

      {/* ─── Tenant routing ──────────────────── */}
      <section className="border-t border-[hsl(var(--brass)/0.25)] pt-10">
        <p className="eyebrow">Tenant routing · Host-header driven</p>
        <h3 className="font-serif text-2xl mt-2">Which experience a visitor gets</h3>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid hsl(var(--brass) / 0.4)" }}>
                <Th>Host</Th><Th>Tenant</Th><Th>Gate</Th><Th>Sign-up</Th><Th>Brand</Th>
              </tr>
            </thead>
            <tbody>
              <Tr cols={["lesconcierge.com / www.lesconcierge.com", "lesconcierges", "ON", "ON", "Les Concierges®"]} />
              <Tr cols={["kvb.mindescapes.in", "kvb", "ON", "OFF", "MindEscapes®"]} />
              <Tr cols={["anything else (preview, IP, etc.)", "global", "OFF", "OFF", "Founder review (no login)"]} muted />
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-[12.5px] text-[hsl(var(--muted))] leading-relaxed max-w-2xl">
          On preview you can simulate any tenant via <code>?_tenant=lesconcierges</code> /
          <code> ?_tenant=kvb</code> — preview-only override, ignored in production.
        </p>
      </section>
    </div>
  );
}

/* ─── Inline sub-components ──────────────────────────── */

function UrlGroup({ eyebrow, title, rows, copied, onCopy, muted, note }) {
  return (
    <section style={muted ? { opacity: 0.78 } : undefined}>
      <p className="eyebrow">{eyebrow}</p>
      <h3 className="font-serif text-2xl mt-2">{title}</h3>
      {note && <p className="mt-2 text-[12px] italic text-[hsl(var(--muted))]">{note}</p>}
      <div className="mt-5 space-y-3">
        {rows.map((row) => {
          const isCopied = copied === row.url;
          return (
            <div key={row.url}
                 className="grid gap-3 p-4 rounded-md border border-[hsl(var(--brass)/0.3)] bg-white/40"
                 style={{ gridTemplateColumns: "minmax(0, 1fr) auto" }}>
              <div style={{ minWidth: 0 }}>
                <button type="button" onClick={() => onCopy(row.url, row.url)}
                        data-testid={`admin-url-${slug(row.url)}`}
                        title="Click to copy"
                        className="font-mono text-[13.5px] text-[hsl(var(--navy))] text-left hover:text-[hsl(var(--brass))] truncate block max-w-full"
                        style={{ cursor: "pointer", background: "transparent", border: "none", padding: 0 }}>
                  {row.url}
                </button>
                <p className="mt-2 text-[12px] leading-snug text-[hsl(var(--muted))]">
                  <b className="text-[hsl(var(--navy))]">{row.who}</b> · {row.what}
                </p>
              </div>
              <div className="self-center">
                <button type="button" onClick={() => onCopy(row.url, row.url)}
                        className="text-[11px] tracking-[0.14em] uppercase px-3 py-1.5 rounded-full border border-[hsl(var(--brass)/0.6)] hover:bg-[hsl(var(--brass)/0.12)]"
                        style={{ color: isCopied ? "hsl(var(--brass))" : "hsl(var(--navy))", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {isCopied ? "✓ Copied" : "Copy"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Th({ children }) {
  return (
    <th className="text-left py-2 pr-4 text-[11px] tracking-[0.14em] uppercase font-medium text-[hsl(var(--muted))]">
      {children}
    </th>
  );
}

function Tr({ cols, muted }) {
  return (
    <tr style={{ borderBottom: "1px solid hsl(var(--brass) / 0.15)", opacity: muted ? 0.7 : 1 }}>
      {cols.map((c, i) => (
        <td key={i} className="py-3 pr-4 align-top"
            style={{ fontFamily: i === 0 ? "ui-monospace, Menlo, monospace" : undefined,
                     fontSize: i === 0 ? 12.5 : 13 }}>
          {c}
        </td>
      ))}
    </tr>
  );
}

function slug(s) {
  return s.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "").slice(0, 60);
}
